import React, { useCallback, useEffect, useState } from "react";
import { CreditCard, Loader2, CheckCircle2, Clock, XCircle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useIdentity } from "@/context/SiteConfigContext";
import { REQUIRES_CONFIRMATION } from "@/lib/constants";
import { useTranslation } from "react-i18next";

const STATUS = {
  pending: { t: "client.payPending", c: "bg-amber-100 text-amber-700 border-amber-200", Icon: Clock },
  paid: { t: "client.payPaid", c: "bg-green-100 text-green-700 border-green-200", Icon: CheckCircle2 },
  expired: { t: "client.payExpired", c: "bg-red-100 text-red-600 border-red-200", Icon: XCircle },
};
const fmtAmt = (l) => `${(l.currency || "usd").toUpperCase()} ${Number(l.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function MyPayments() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const identity = useIdentity();
  const { t } = useTranslation();

  const load = useCallback(async () => {
    try { const r = await api.get("/my/payment-links"); setLinks(r.data.links || []); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => {
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, [load]);

  if (loading || links.length === 0) return null;

  const pay = async (l) => {
    if (l.status === "paid") return;
    if (l.status === "expired") {
      try {
        const r = await api.post(`/my/payment-links/${l.session_id}/refresh`);
        window.open(r.data.url, "_blank", "noopener");
        await load();
      } catch { toast.error(t("client.payOpenError")); }
      return;
    }
    window.open(l.url, "_blank", "noopener");
  };

  return (
    <div className="mt-10" data-testid="my-payments">
      <div className="flex items-center gap-2 mb-4">
        <CreditCard className="h-5 w-5 text-orange-700" />
        <h2 className="font-heading text-xl tracking-tight text-foreground">{t("client.myPayments")}</h2>
      </div>
      <div className="space-y-3">
        {links.map((l) => {
          const s = STATUS[l.status] || STATUS.pending;
          return (
            <div key={l.session_id} className="rounded-xl border border-border bg-card p-5 card-soft" data-testid={`my-pay-${l.session_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-foreground">{l.service_name}</span>
                    <span className={`inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[10px] uppercase tracking-wider ${s.c}`} data-testid={`my-pay-status-${l.session_id}`}><s.Icon className="h-3 w-3" /> {t(s.t)}</span>
                  </div>
                  {l.description && <p className="mt-1 text-xs text-muted-foreground">{l.description}</p>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-mono text-lg text-primary">{fmtAmt(l)}</span>
                  {l.status === "paid" ? (
                    <span className="inline-flex items-center gap-1 text-sm text-green-700"><CheckCircle2 className="h-4 w-4" /> {t("client.payPaid")}</span>
                  ) : (
                    <button onClick={() => pay(l)} className="inline-flex items-center rounded-sm bg-orange-500 text-white px-4 py-2 text-sm hover:bg-orange-600" data-testid={`my-pay-now-${l.session_id}`}>{t("client.payNow")} <ExternalLink className="h-3.5 w-3.5 ml-1.5" /></button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-[11px] leading-relaxed text-muted-foreground/80" data-testid="my-payments-legal-line">
        {t("client.billedBy", { name: identity.legalName })}{identity.regNumber !== REQUIRES_CONFIRMATION ? ` · ${t("client.regNo", { reg: identity.regNumber })}` : ""} · {identity.office}
      </p>
    </div>
  );
}
